import React, { useState, useEffect, useMemo } from 'react'
import { useI18n } from '../../i18n'
import { useAuth } from '../../components/AuthProvider'
import { Card, CardContent, CardHeader } from '../../components/ui'
import { API_ENDPOINTS } from '../../config/api'
import { Plus, Edit, Trash2, Eye, X, CheckCircle, Clock, XCircle, Calendar, TrendingUp, AlertTriangle } from 'lucide-react'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { PageLayout } from '../../layouts/PageLayout'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Select'
import { SearchField } from '../../components/ui/SearchField'
import { GovernanceListingSection } from '../../components/governance/GovernanceListingSection'
import { GovernanceInlineFormPanel } from '../../components/governance/GovernanceInlineFormPanel'
import { TableEmptyRow, TableErrorRow, TableLoadingRow } from '../../components/governance/TableBodyStates'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'

interface AuditFinding {
  id: string
  title: string
  description?: string
  severity: string
  status: string
  dueDate?: string
  ownerName?: string
  createdAt: string
}

interface Audit {
  id: string
  name: string
  scope?: string
  plannedDate: string
  completedAt?: string
  status: string
  ownerName?: string
  ownerId?: string
  notes?: string
  findings: AuditFinding[]
  _count: {
    findings: number
  }
}

const GovernanceAudits: React.FC = () => {
  const { t } = useI18n()
  const { token } = useAuth()
  const [audits, setAudits] = useState<Audit[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [findingModalOpen, setFindingModalOpen] = useState(false)
  const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null)
  const [selectedFinding, setSelectedFinding] = useState<AuditFinding | null>(null)
  const [users, setUsers] = useState<Array<{ id: string; username: string; email?: string }>>([])
  const [progressTrend, setProgressTrend] = useState<any[]>([])
  const [findingsForecast, setFindingsForecast] = useState<any>(null)
  const [generatingPlan, setGeneratingPlan] = useState(false)
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  useEffect(() => {
    loadAudits()
    loadUsers()
    loadAnalytics()
  }, [])

  useEffect(() => {
    loadAudits()
  }, [statusFilter, searchQuery, fromDate, toDate])

  const loadAnalytics = async () => {
    try {
      const [trendRes, forecastRes] = await Promise.all([
        fetch(API_ENDPOINTS.GOVERNANCE_AUDITS_ANALYTICS_PROGRESS_TREND, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(API_ENDPOINTS.GOVERNANCE_AUDITS_ANALYTICS_FINDINGS_FORECAST, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ])

      if (trendRes.ok) {
        const trendData = await trendRes.json()
        const primaryTrend = Array.isArray(trendData?.trend)
          ? trendData.trend
          : Array.isArray(trendData?.series)
            ? trendData.series
            : []
        const normalizedPrimary = primaryTrend
          .map((item: any) => ({
            month: String(item?.month || item?.date || ''),
            planned: Number(item?.planned || 0),
            completed: Number(item?.completed || item?.closed || item?.value || 0),
            inProgress: Number(item?.inProgress || 0),
            missed: Number(item?.missed || 0),
          }))
          .filter((item: any) => item.month)
        if (normalizedPrimary.length > 0) {
          setProgressTrend(normalizedPrimary)
        } else {
          const readinessRes = await fetch(API_ENDPOINTS.AUDIT_ANALYTICS_READINESS_HISTORY, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
          if (readinessRes.ok) {
            const readinessData = await readinessRes.json()
            const fallbackTrend = Array.isArray(readinessData?.data)
              ? readinessData.data.map((item: any) => ({
                  month: String(item?.date || ''),
                  planned: 0,
                  completed: Number(item?.value || 0),
                  inProgress: 0,
                  missed: 0,
                })).filter((item: any) => item.month)
              : []
            setProgressTrend(fallbackTrend)
          } else {
            setProgressTrend([])
          }
        }
      }

      if (forecastRes.ok) {
        const forecastData = await forecastRes.json()
        setFindingsForecast(forecastData.forecast || null)
      }
    } catch (err) {
      console.error('[GovernanceAudits] Error loading analytics:', err)
    }
  }

  const handleGenerateAnnualPlan = async () => {
    if (!confirm(t('governance.audits.generatePlanConfirm', 'Generate annual audit plan for next year? This will create new planned audits based on previous year.'))) {
      return
    }

    try {
      setGeneratingPlan(true)
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_AUDITS_GENERATE_ANNUAL_PLAN, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ year: new Date().getFullYear() + 1 })
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to generate annual plan')
      }

      const data = await res.json()
      alert(t('governance.audits.generatePlanSuccess', `Successfully generated ${data.audits?.length || 0} audit(s) for ${new Date().getFullYear() + 1}`))
      await loadAudits()
      await loadAnalytics()
    } catch (err: any) {
      alert(err.message || t('governance.audits.generatePlanError', 'Failed to generate annual plan'))
    } finally {
      setGeneratingPlan(false)
    }
  }

  const loadUsers = async () => {
    try {
      const rawUser = localStorage.getItem('user')
      const userObj = rawUser ? JSON.parse(rawUser) : null
      const primary = userObj?.organizations?.[0]
      const orgId = (primary && typeof primary === 'object' && primary.id) ? primary.id : (typeof primary === 'string' ? primary : undefined)
      
      if (!orgId) {
        setUsers([])
        return
      }
      
      const response = await fetch(`/api/users?organizationId=${orgId}&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      if (response.ok) {
        const data = await response.json()
        setUsers(Array.isArray(data.users) ? data.users : [])
      }
    } catch (err) {
      console.error('[GovernanceAudits] Error loading users:', err)
      setUsers([])
    }
  }

  const loadAudits = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = new URLSearchParams()
      if (statusFilter) params.append('status', statusFilter)
      if (searchQuery) params.append('search', searchQuery)
      if (fromDate) params.append('fromDate', fromDate)
      if (toDate) params.append('toDate', toDate)

      const url = `${API_ENDPOINTS.GOVERNANCE_AUDITS}${params.toString() ? `?${params.toString()}` : ''}`
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!res.ok) throw new Error('Failed to load audits')
      const data = await res.json()
      setAudits(data.audits || [])
    } catch (err: any) {
      setError(err.message || 'Failed to load audits')
      console.error('[GovernanceAudits] Error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteAudit = async (id: string) => {
    if (!confirm(t('governance.audit.deleteConfirm', 'Are you sure you want to delete this audit?'))) {
      return
    }

    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_AUDIT(id), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to delete audit')
      }

      await loadAudits()
    } catch (err: any) {
      alert(err.message || t('governance.audit.deleteError', 'Failed to delete audit'))
    }
  }

  // Calculate overview metrics
  const overview = useMemo(() => {
    const planned = audits.filter(a => a.status === 'Planned').length
    const inProgress = audits.filter(a => a.status === 'InProgress').length
    const completed = audits.filter(a => a.status === 'Completed').length
    const missed = audits.filter(a => a.status === 'Missed').length
    const totalFindings = audits.reduce((sum, a) => sum + (a._count?.findings || 0), 0)
    const openFindings = audits.reduce((sum, a) => {
      return sum + a.findings.filter(f => f.status === 'Open').length
    }, 0)

    return { planned, inProgress, completed, missed, totalFindings, openFindings }
  }, [audits])

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Completed':
        return listStyles.badgeCompleted
      case 'InProgress':
        return listStyles.badgeInProgress
      case 'Planned':
        return listStyles.badgePlanned
      case 'Missed':
        return listStyles.badgeMissed
      default:
        return listStyles.badgeDefault
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'High': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      case 'Medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'Low': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed': return <CheckCircle className={listStyles.badgeIcon} aria-hidden />
      case 'InProgress': return <Clock className={listStyles.badgeIcon} aria-hidden />
      case 'Missed': return <XCircle className={listStyles.badgeIcon} aria-hidden />
      default: return <Clock className={listStyles.badgeIcon} aria-hidden />
    }
  }

  const auditStatusFilterOptions = useMemo(
    () => [
      { value: 'Planned', label: t('governance.audits.planned', 'Planned') },
      { value: 'InProgress', label: t('governance.audits.inProgress', 'In Progress') },
      { value: 'Completed', label: t('governance.audits.completed', 'Completed') },
      { value: 'Missed', label: t('governance.audits.missed', 'Missed') },
    ],
    [t],
  )

  return (
    <PageLayout pageScope="governance-audits-page" title={t('governance.audits.title', 'Internal Audit Center')}
      actions={
        <div className={listStyles.headerActions}>
          <Button
            type="button"
            size="small"
            color="primary"
            icon={<Calendar className={listStyles.badgeIcon} aria-hidden />}
            onClick={handleGenerateAnnualPlan}
            disabled={generatingPlan}
          >
            {generatingPlan ? t('governance.audits.generating', 'Generating...') : t('governance.audits.generatePlan', 'Generate Annual Plan')}
          </Button>
          <Button
            type="button"
            size="small"
            color="primary"
            icon={<Plus className={listStyles.badgeIcon} aria-hidden />}
            onClick={() => {
              setSelectedAudit(null)
              setCreateModalOpen(true)
            }}
            className={listStyles.btnGreen}
          >
            {t('governance.audits.create', 'New Audit')}
          </Button>
        </div>
      }
    >
      <GovernanceListingSection title={t('governance.audits.overview', 'Audit Overview')}>
        <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid6}`}>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileYellow}`}>
            <p className={listStyles.metricTileLabel}>{t('governance.audits.planned', 'Planned')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textYellow}`}>{overview.planned}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileBlue}`}>
            <p className={listStyles.metricTileLabel}>{t('governance.audits.inProgress', 'In Progress')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textBlue}`}>{overview.inProgress}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileGreen}`}>
            <p className={listStyles.metricTileLabel}>{t('governance.audits.completed', 'Completed')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textGreen}`}>{overview.completed}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileRed}`}>
            <p className={`${listStyles.metricTileLabel} ${listStyles.actionsGap}`}>
              {t('governance.audits.missed', 'Missed')}
              {overview.missed > 0 && <AlertTriangle className={listStyles.badgeIcon} aria-hidden />}
            </p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textRed}`}>{overview.missed}</p>
            {overview.missed > 0 && (
              <p className={listStyles.metricTileHint}>
                {t('governance.audits.missedWarning', 'Action required')}
              </p>
            )}
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTilePurple}`}>
            <p className={listStyles.metricTileLabel}>{t('governance.audits.totalFindings', 'Total Findings')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textPurple}`}>{overview.totalFindings}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileOrange}`}>
            <p className={listStyles.metricTileLabel}>{t('governance.audits.openFindings', 'Open Findings')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textOrange}`}>{overview.openFindings}</p>
          </div>
        </div>
      </GovernanceListingSection>

      <div className={listStyles.chartsRow}>
        {progressTrend.length > 0 && (
          <Card>
            <CardHeader>
              <h3 className={listStyles.chartCardTitle}>
                <TrendingUp className={listStyles.chartIcon} aria-hidden />
                {t('governance.audits.progressTrend', 'Audit Progress Trend')}
              </h3>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={progressTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="planned" stroke="#FACC15" name={t('governance.audits.planned', 'Planned')} />
                  <Line type="monotone" dataKey="completed" stroke="#10B981" name={t('governance.audits.completed', 'Completed')} />
                  <Line type="monotone" dataKey="inProgress" stroke="#3B82F6" name={t('governance.audits.inProgress', 'In Progress')} />
                  <Line type="monotone" dataKey="missed" stroke="#EF4444" name={t('governance.audits.missed', 'Missed')} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Open Findings Forecast */}
        {findingsForecast && (
          <Card>
            <CardHeader>
              <h3 className={listStyles.chartCardTitle}>
                <TrendingUp className={listStyles.chartIcon} aria-hidden />
                {t('governance.audits.findingsForecast', 'Open Findings Forecast')}
              </h3>
            </CardHeader>
            <CardContent>
              <div className={listStyles.forecastLine}>
                {t('governance.audits.currentOpen', 'Current Open')}:{' '}
                <span className={listStyles.forecastValue}>{findingsForecast.currentOpen}</span>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={findingsForecast.forecast}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="projectedOpen" fill="#F59E0B" name={t('governance.audits.projectedOpen', 'Projected Open')} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      <GovernanceListingSection
        title={t('governance.audits.list', 'Audits List')}
        description={t(
          'governance.audits.listSubtitle',
          'Search, filter by status and date range, then open or edit an audit.',
        )}
        toolbar={
          <div className={listStyles.filtersRow}>
            <div className={`${listStyles.toolbarField} ${listStyles.filtersToolbarGrow}`}>
              <SearchField
                label={t('governance.audits.searchLabel', 'Search')}
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={t('governance.audits.searchPlaceholder', 'Search audits...')}
                minWidth="14rem"
              />
            </div>
            <div className={listStyles.toolbarField}>
              <Select<string>
                label={t('governance.audits.table.status', 'Status')}
                minWidth="11rem"
                placeholder={t('governance.audits.filterStatus', 'All Statuses')}
                options={auditStatusFilterOptions}
                value={statusFilter === '' ? null : statusFilter}
                onChange={(v) => setStatusFilter(v ?? '')}
              />
            </div>
            <div className={listStyles.toolbarField}>
              <label className={listStyles.fieldLabel} htmlFor="governance-audits-from">
                {t('governance.audits.fromDate', 'From')}
              </label>
              <input
                id="governance-audits-from"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={listStyles.dateInput}
              />
            </div>
            <div className={listStyles.toolbarField}>
              <label className={listStyles.fieldLabel} htmlFor="governance-audits-to">
                {t('governance.audits.toDate', 'To')}
              </label>
              <input
                id="governance-audits-to"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={listStyles.dateInput}
              />
            </div>
            {(statusFilter || searchQuery || fromDate || toDate) && (
              <div className={`${listStyles.toolbarField} ${listStyles.toolbarFieldAction}`}>
                <Button
                  type="button"
                  size="small"
                  color="outline"
                  icon={<X className={listStyles.badgeIcon} aria-hidden />}
                  onClick={() => {
                    setStatusFilter('')
                    setSearchQuery('')
                    setFromDate('')
                    setToDate('')
                  }}
                >
                  {t('governance.audits.clearFilters', 'Clear')}
                </Button>
              </div>
            )}
          </div>
        }
      >
        {createModalOpen && !editModalOpen ? (
          <AuditModal
            open={createModalOpen}
            onClose={() => {
              setCreateModalOpen(false)
              setSelectedAudit(null)
            }}
            audit={null}
            users={users}
            onSuccess={loadAudits}
          />
        ) : null}
        <div className={listStyles.tableWrap}>
          <table className={listStyles.table}>
            <thead>
              <tr>
                <th className={listStyles.th} scope="col">
                  {t('governance.audits.table.name', 'Name')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.audits.table.scope', 'Scope')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.audits.table.plannedDate', 'Planned Date')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.audits.table.completedAt', 'Completed')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.audits.table.status', 'Status')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.audits.table.findings', 'Findings')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.audits.table.owner', 'Owner')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.audits.table.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <TableLoadingRow colSpan={8}>{t('common.loading', 'Loading...')}</TableLoadingRow>
              )}
              {!loading && error && (
                <TableErrorRow colSpan={8}>{error}</TableErrorRow>
              )}
              {!loading && !error && audits.length === 0 && (
                <TableEmptyRow colSpan={8}>
                  {t('governance.audits.noAudits', 'No audits found. Create your first audit to get started.')}
                </TableEmptyRow>
              )}
              {!loading &&
                !error &&
                audits.map((audit) => (
                  <tr key={audit.id} className={listStyles.row}>
                    <td className={listStyles.td}>
                      <div className={listStyles.cellStrong}>{audit.name}</div>
                    </td>
                    <td className={listStyles.td}>
                      {audit.scope ? (
                        <div className={`${listStyles.cellMuted} ${listStyles.lineClamp2}`}>{audit.scope}</div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={listStyles.td}>{new Date(audit.plannedDate).toLocaleDateString()}</td>
                    <td className={listStyles.td}>
                      {audit.completedAt ? new Date(audit.completedAt).toLocaleDateString() : '—'}
                    </td>
                    <td className={listStyles.td}>
                      <span className={`${listStyles.badge} ${getStatusBadgeClass(audit.status)}`}>
                        {getStatusIcon(audit.status)}
                        {audit.status}
                      </span>
                    </td>
                    <td className={listStyles.td}>
                      <div className={listStyles.actionsGap}>
                        <span className={listStyles.cellStrong}>{audit._count?.findings || 0}</span>
                        {audit.findings.filter((f) => f.status === 'Open').length > 0 && (
                          <span className={listStyles.textDanger}>
                            ({audit.findings.filter((f) => f.status === 'Open').length}{' '}
                            {t('governance.audits.open', 'open')})
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={listStyles.td}>{audit.ownerName || '—'}</td>
                    <td className={listStyles.td}>
                      <div className={listStyles.actionsGap}>
                        <Button
                          type="button"
                          size="small"
                          color="outline"
                          iconOnly
                          icon={<Eye className={listStyles.badgeIcon} aria-hidden />}
                          onClick={() => {
                            loadAuditDetails(audit.id)
                          }}
                          className="border-0 bg-transparent p-1 shadow-none text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                          title={t('governance.audits.view', 'View details')}
                        />
                        <Button
                          type="button"
                          size="small"
                          color="outline"
                          iconOnly
                          icon={<Edit className={listStyles.badgeIcon} aria-hidden />}
                          onClick={() => {
                            setSelectedAudit(audit)
                            setEditModalOpen(true)
                          }}
                          className="border-0 bg-transparent p-1 shadow-none text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded"
                          title={t('governance.audits.edit', 'Edit')}
                        />
                        <Button
                          type="button"
                          size="small"
                          color="outline"
                          iconOnly
                          icon={<Trash2 className={listStyles.badgeIcon} aria-hidden />}
                          onClick={() => handleDeleteAudit(audit.id)}
                          className="border-0 bg-transparent p-1 shadow-none text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          title={t('governance.audits.delete', 'Delete')}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </GovernanceListingSection>

      {/* Modals */}
      {editModalOpen && selectedAudit ? (
        <AuditModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false)
            setSelectedAudit(null)
          }}
          audit={selectedAudit}
          users={users}
          onSuccess={loadAudits}
        />
      ) : null}

      {viewModalOpen && selectedAudit && (
        <AuditViewModal
          open={viewModalOpen}
          onClose={() => {
            setViewModalOpen(false)
            setSelectedAudit(null)
          }}
          audit={selectedAudit}
          onFindingEdit={(finding) => {
            setSelectedFinding(finding)
            setFindingModalOpen(true)
          }}
          onFindingAdd={() => {
            setSelectedFinding(null)
            setFindingModalOpen(true)
          }}
          onSuccess={loadAudits}
        />
      )}

      {findingModalOpen && selectedAudit && (
        <FindingModal
          open={findingModalOpen}
          onClose={() => {
            setFindingModalOpen(false)
            setSelectedFinding(null)
          }}
          auditId={selectedAudit.id}
          finding={selectedFinding}
          onSuccess={async () => {
            await loadAudits()
            if (selectedAudit) {
              await loadAuditDetails(selectedAudit.id)
            }
          }}
        />
      )}
    </PageLayout>
  )

  async function loadAuditDetails(id: string) {
    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_AUDIT(id), {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setSelectedAudit(data.audit)
        setViewModalOpen(true)
      }
    } catch (err) {
      console.error('[GovernanceAudits] Error loading audit details:', err)
    }
  }
}

// Audit Create/Edit Modal Component
interface AuditModalProps {
  open: boolean
  onClose: () => void
  audit: Audit | null
  users: Array<{ id: string; username: string; email?: string }>
  onSuccess: () => void
}

const AuditModal: React.FC<AuditModalProps> = ({ open, onClose, audit, users, onSuccess }) => {
  const { t } = useI18n()
  const { token } = useAuth()
  const auditFormStatusOptions = useMemo(
    () => [
      { value: 'Planned', label: t('governance.audit.form.statusPlanned', 'Planned') },
      { value: 'InProgress', label: t('governance.audit.form.statusInProgress', 'In Progress') },
      { value: 'Completed', label: t('governance.audit.form.statusCompleted', 'Completed') },
      { value: 'Missed', label: t('governance.audit.form.statusMissed', 'Missed') },
    ],
    [t],
  )
  const ownerSelectOptions = useMemo(
    () => [
      { value: '', label: t('governance.audit.form.noOwner', 'None') },
      ...users.map((u) => ({
        value: u.id,
        label: `${u.username}${u.email ? ` (${u.email})` : ''}`,
      })),
    ],
    [t, users],
  )
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: audit?.name || '',
    scope: audit?.scope || '',
    plannedDate: audit?.plannedDate ? new Date(audit.plannedDate).toISOString().split('T')[0] : '',
    completedAt: audit?.completedAt ? new Date(audit.completedAt).toISOString().split('T')[0] : '',
    status: audit?.status || 'Planned',
    ownerName: audit?.ownerName || '',
    ownerId: audit?.ownerId || '',
    notes: audit?.notes || ''
  })
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setFormData({
        name: audit?.name || '',
        scope: audit?.scope || '',
        plannedDate: audit?.plannedDate ? new Date(audit.plannedDate).toISOString().split('T')[0] : '',
        completedAt: audit?.completedAt ? new Date(audit.completedAt).toISOString().split('T')[0] : '',
        status: audit?.status || 'Planned',
        ownerName: audit?.ownerName || '',
        ownerId: audit?.ownerId || '',
        notes: audit?.notes || ''
      })
      setError(null)
    }
  }, [open, audit])

  if (!open) return null

  const formBody = (
    <>
      <div className={listStyles.formField}>
        <label className={listStyles.formFieldLabel} htmlFor="audit-form-name">
          {t('governance.audit.form.name', 'Name')} *
        </label>
        <input
          id="audit-form-name"
          type="text"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className={listStyles.formInput}
        />
      </div>
      <div className={listStyles.formField}>
        <label className={listStyles.formFieldLabel} htmlFor="audit-form-scope">
          {t('governance.audit.form.scope', 'Scope')}
        </label>
        <textarea
          id="audit-form-scope"
          value={formData.scope}
          onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
          rows={2}
          className={listStyles.formTextarea}
        />
      </div>
      <div className={listStyles.formGrid2}>
        <div className={listStyles.formField}>
          <label className={listStyles.formFieldLabel} htmlFor="audit-form-planned">
            {t('governance.audit.form.plannedDate', 'Planned Date')} *
          </label>
          <input
            id="audit-form-planned"
            type="date"
            required
            value={formData.plannedDate}
            onChange={(e) => setFormData({ ...formData, plannedDate: e.target.value })}
            className={listStyles.dateInput}
          />
        </div>
        <div className={listStyles.formField}>
          <label className={listStyles.formFieldLabel}>{t('governance.audit.form.status', 'Status')}</label>
          <Select<string>
            className="w-full"
            minWidth="100%"
            options={auditFormStatusOptions}
            value={formData.status}
            onChange={(v) => v && setFormData({ ...formData, status: v })}
          />
        </div>
      </div>
      {audit ? (
        <div className={listStyles.formField}>
          <label className={listStyles.formFieldLabel} htmlFor="audit-form-completed">
            {t('governance.audit.form.completedAt', 'Completed At')}
          </label>
          <input
            id="audit-form-completed"
            type="date"
            value={formData.completedAt}
            onChange={(e) => setFormData({ ...formData, completedAt: e.target.value })}
            className={listStyles.dateInput}
          />
        </div>
      ) : null}
      <div className={listStyles.formGrid2}>
        <div className={listStyles.formField}>
          <label className={listStyles.formFieldLabel} htmlFor="audit-form-owner-name">
            {t('governance.audit.form.ownerName', 'Owner Name')}
          </label>
          <input
            id="audit-form-owner-name"
            type="text"
            value={formData.ownerName}
            onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
            className={listStyles.formInput}
          />
        </div>
        <div className={listStyles.formField}>
          <label className={listStyles.formFieldLabel}>{t('governance.audit.form.ownerId', 'Owner (User)')}</label>
          <Select<string>
            className="w-full"
            minWidth="100%"
            options={ownerSelectOptions}
            value={formData.ownerId}
            onChange={(v) => setFormData({ ...formData, ownerId: v ?? '' })}
          />
        </div>
      </div>
      <div className={listStyles.formField}>
        <label className={listStyles.formFieldLabel} htmlFor="audit-form-notes">
          {t('governance.audit.form.notes', 'Notes')}
        </label>
        <textarea
          id="audit-form-notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={3}
          className={listStyles.formTextarea}
        />
      </div>
      {error ? <div className={listStyles.formError}>{error}</div> : null}
    </>
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim() || !formData.plannedDate) {
      setError(t('governance.audit.form.nameAndDateRequired', 'Name and planned date are required'))
      return
    }

    try {
      setLoading(true)
      setError(null)

      const url = audit 
        ? API_ENDPOINTS.GOVERNANCE_AUDIT(audit.id)
        : API_ENDPOINTS.GOVERNANCE_AUDITS
      const method = audit ? 'PUT' : 'POST'

      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          scope: formData.scope || undefined,
          plannedDate: formData.plannedDate,
          completedAt: formData.completedAt || undefined,
          status: formData.status,
          ownerName: formData.ownerName || undefined,
          ownerId: formData.ownerId || undefined,
          notes: formData.notes || undefined
        })
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || (audit ? 'Failed to update audit' : 'Failed to create audit'))
      }

      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err.message || t('governance.audit.saveError', 'Failed to save audit'))
    } finally {
      setLoading(false)
    }
  }

  const formId = audit ? 'governance-audit-edit-form' : 'governance-audit-create-form'
  const title = audit
    ? t('governance.audit.editTitle', 'Edit Audit')
    : t('governance.audit.createTitle', 'New Audit')

  const footer = (
    <>
      <Button type="button" color="outline" size="small" onClick={onClose} disabled={loading}>
        {t('common.cancel', 'Cancel')}
      </Button>
      <Button
        type="submit"
        form={formId}
        color="primary"
        size="small"
        disabled={loading || !formData.name.trim() || !formData.plannedDate}
      >
        {loading ? t('common.saving', 'Saving…') : audit ? t('common.save', 'Save') : t('common.create', 'Create')}
      </Button>
    </>
  )

  if (!audit) {
    return (
      <GovernanceInlineFormPanel title={title} onClose={onClose} footer={footer}>
        <form id={formId} onSubmit={handleSubmit}>
          {formBody}
        </form>
      </GovernanceInlineFormPanel>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="relative w-full max-w-2xl bg-white dark:bg-neutral-900 rounded-xl shadow-xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <Button
            type="button"
            size="small"
            color="outline"
            iconOnly
            icon={<X className="w-5 h-5" />}
            onClick={onClose}
            disabled={loading}
            className="border-0 bg-transparent p-1 shadow-none rounded hover:bg-gray-100 dark:hover:bg-gray-800"
            title={t('common.close', 'Close')}
          />
        </div>

        <form id={formId} onSubmit={handleSubmit}>
          {formBody}
          <div className={listStyles.inlineFormActions}>{footer}</div>
        </form>
      </div>
    </div>
  )
}

// Audit View Modal Component (with findings)
interface AuditViewModalProps {
  open: boolean
  onClose: () => void
  audit: Audit
  onFindingEdit: (finding: AuditFinding) => void
  onFindingAdd: () => void
  onSuccess: () => void
}

const AuditViewModal: React.FC<AuditViewModalProps> = ({ open, onClose, audit, onFindingEdit, onFindingAdd, onSuccess }) => {
  const { t } = useI18n()
  const { token } = useAuth()

  if (!open) return null

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'High': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      case 'Medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'Low': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Closed': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'InProgress': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'Open': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    }
  }

  const handleDeleteFinding = async (findingId: string) => {
    if (!confirm(t('governance.audit.finding.deleteConfirm', 'Are you sure you want to delete this finding?'))) {
      return
    }

    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_AUDIT_FINDING(audit.id, findingId), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to delete finding')
      }

      onSuccess()
    } catch (err: any) {
      alert(err.message || t('governance.audit.finding.deleteError', 'Failed to delete finding'))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="relative w-full max-w-4xl bg-white dark:bg-neutral-900 rounded-xl shadow-xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{audit.name}</h3>
          <Button
            type="button"
            size="small"
            color="outline"
            iconOnly
            icon={<X className="w-5 h-5" />}
            onClick={onClose}
            className="border-0 bg-transparent p-1 shadow-none rounded hover:bg-gray-100 dark:hover:bg-gray-800"
            title={t('common.close', 'Close')}
          />
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('governance.audit.form.scope', 'Scope')}</label>
            <p className="mt-1">{audit.scope || '-'}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('governance.audit.form.plannedDate', 'Planned Date')}</label>
              <p className="mt-1">{new Date(audit.plannedDate).toLocaleDateString()}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('governance.audit.form.status', 'Status')}</label>
              <p className="mt-1">{audit.status}</p>
            </div>
          </div>
          {audit.notes && (
            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('governance.audit.form.notes', 'Notes')}</label>
              <p className="mt-1">{audit.notes}</p>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold">{t('governance.audit.findings', 'Findings')}</h4>
            <Button
              type="button"
              size="small"
              color="primary"
              icon={<Plus className="w-4 h-4" />}
              onClick={onFindingAdd}
              className="!bg-green-600 hover:!bg-green-700 !border-green-600"
            >
              {t('governance.audit.addFinding', 'Add Finding')}
            </Button>
          </div>

          {audit.findings.length === 0 ? (
            <p className="text-gray-500 text-center py-4">{t('governance.audit.noFindings', 'No findings yet')}</p>
          ) : (
            <div className="space-y-2">
              {audit.findings.map((finding) => (
                <div key={finding.id} className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h5 className="font-medium">{finding.title}</h5>
                        <span className={`px-2 py-0.5 rounded text-xs ${getSeverityColor(finding.severity)}`}>
                          {finding.severity}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(finding.status)}`}>
                          {finding.status}
                        </span>
                      </div>
                      {finding.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{finding.description}</p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        {finding.ownerName && (
                          <span>{t('governance.audit.finding.owner', 'Owner')}: {finding.ownerName}</span>
                        )}
                        {finding.dueDate && (
                          <span>{t('governance.audit.finding.dueDate', 'Due')}: {new Date(finding.dueDate).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-4">
                      <Button
                        type="button"
                        size="small"
                        color="outline"
                        iconOnly
                        icon={<Edit className="w-4 h-4" />}
                        onClick={() => onFindingEdit(finding)}
                        className="border-0 bg-transparent p-1 shadow-none text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded"
                        title={t('governance.audit.finding.edit', 'Edit')}
                      />
                      <Button
                        type="button"
                        size="small"
                        color="outline"
                        iconOnly
                        icon={<Trash2 className="w-4 h-4" />}
                        onClick={() => handleDeleteFinding(finding.id)}
                        className="border-0 bg-transparent p-1 shadow-none text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        title={t('governance.audit.finding.delete', 'Delete')}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Finding Create/Edit Modal Component
interface FindingModalProps {
  open: boolean
  onClose: () => void
  auditId: string
  finding: AuditFinding | null
  onSuccess: () => void
}

const FindingModal: React.FC<FindingModalProps> = ({ open, onClose, auditId, finding, onSuccess }) => {
  const { t } = useI18n()
  const { token } = useAuth()
  const findingSeverityOptions = useMemo(
    () => [
      { value: 'Low', label: t('governance.audit.finding.form.severityLow', 'Low') },
      { value: 'Medium', label: t('governance.audit.finding.form.severityMedium', 'Medium') },
      { value: 'High', label: t('governance.audit.finding.form.severityHigh', 'High') },
      { value: 'Critical', label: t('governance.audit.finding.form.severityCritical', 'Critical') },
    ],
    [t],
  )
  const findingStatusFormOptions = useMemo(
    () => [
      { value: 'Open', label: t('governance.audit.finding.form.statusOpen', 'Open') },
      { value: 'InProgress', label: t('governance.audit.finding.form.statusInProgress', 'In Progress') },
      { value: 'Closed', label: t('governance.audit.finding.form.statusClosed', 'Closed') },
    ],
    [t],
  )
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    title: finding?.title || '',
    description: finding?.description || '',
    severity: finding?.severity || 'Medium',
    status: finding?.status || 'Open',
    dueDate: finding?.dueDate ? new Date(finding.dueDate).toISOString().split('T')[0] : '',
    ownerName: finding?.ownerName || ''
  })
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setFormData({
        title: finding?.title || '',
        description: finding?.description || '',
        severity: finding?.severity || 'Medium',
        status: finding?.status || 'Open',
        dueDate: finding?.dueDate ? new Date(finding.dueDate).toISOString().split('T')[0] : '',
        ownerName: finding?.ownerName || ''
      })
      setError(null)
    }
  }, [open, finding])

  if (!open) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.title.trim()) {
      setError(t('governance.audit.finding.form.titleRequired', 'Title is required'))
      return
    }

    try {
      setLoading(true)
      setError(null)

      const url = finding
        ? API_ENDPOINTS.GOVERNANCE_AUDIT_FINDING(auditId, finding.id)
        : API_ENDPOINTS.GOVERNANCE_AUDIT_FINDINGS(auditId)
      const method = finding ? 'PUT' : 'POST'

      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description || undefined,
          severity: formData.severity,
          status: formData.status,
          dueDate: formData.dueDate || undefined,
          ownerName: formData.ownerName || undefined
        })
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || (finding ? 'Failed to update finding' : 'Failed to create finding'))
      }

      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err.message || t('governance.audit.finding.saveError', 'Failed to save finding'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="relative w-full max-w-2xl bg-white dark:bg-neutral-900 rounded-xl shadow-xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {finding 
              ? t('governance.audit.finding.editTitle', 'Edit Finding')
              : t('governance.audit.finding.createTitle', 'Add Finding')
            }
          </h3>
          <Button
            type="button"
            size="small"
            color="outline"
            iconOnly
            icon={<X className="w-5 h-5" />}
            onClick={onClose}
            disabled={loading}
            className="border-0 bg-transparent p-1 shadow-none rounded hover:bg-gray-100 dark:hover:bg-gray-800"
            title={t('common.close', 'Close')}
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('governance.audit.finding.form.title', 'Title')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-800"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              {t('governance.audit.finding.form.description', 'Description')}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-800"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.audit.finding.form.severity', 'Severity')}
              </label>
              <Select<string>
                className="w-full"
                minWidth="100%"
                options={findingSeverityOptions}
                value={formData.severity}
                onChange={(v) => v && setFormData({ ...formData, severity: v })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.audit.finding.form.status', 'Status')}
              </label>
              <Select<string>
                className="w-full"
                minWidth="100%"
                options={findingStatusFormOptions}
                value={formData.status}
                onChange={(v) => v && setFormData({ ...formData, status: v })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.audit.finding.form.dueDate', 'Due Date')}
              </label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.audit.finding.form.ownerName', 'Owner Name')}
              </label>
              <input
                type="text"
                value={formData.ownerName}
                onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-800"
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" color="outline" size="small" onClick={onClose} disabled={loading}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              color="primary"
              size="small"
              disabled={loading || !formData.title.trim()}
            >
              {loading ? t('common.saving', 'Saving...') : (finding ? t('common.save', 'Save') : t('common.create', 'Create'))}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default GovernanceAudits
